import React, { useState, useEffect } from 'react';
import { fetchAllRenders, uploadRender, deleteRender } from '../api/renders';
import '../styles/AdminPage.css';

const AdminPage = () => {
  const [renders, setRenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    thumbnailFile: null,
    fullImageFile: null,
    software: 'Blender',
    renderTime: '',
  });
  const [uploadStatus, setUploadStatus] = useState({
    uploading: false,
    success: false,
    error: null
  });
  const [deleteStatus, setDeleteStatus] = useState({
    deleting: false,
    id: null,
    error: null
  });

  useEffect(() => {
    loadRenders();
  }, []);

  const loadRenders = async () => {
    try {
      setLoading(true);
      const data = await fetchAllRenders();
      setRenders(data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch renders:', err);
      setError('Failed to load renders. Please try again later.');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title || !formData.description || !formData.thumbnailFile || !formData.fullImageFile) {
      setUploadStatus({
        uploading: false,
        success: false,
        error: "Please fill in all required fields and upload both images."
      });
      return;
    }
    
    setUploadStatus({ uploading: true, success: false, error: null });
    
    try {
      const newRender = await uploadRender(formData);
      
      setUploadStatus({
        uploading: false,
        success: true,
        error: null
      });
      
      // Add the new render to the list
      setRenders(prev => [newRender, ...prev]);
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        thumbnailFile: null,
        fullImageFile: null,
        software: 'Blender',
        renderTime: '',
      });
      
      // Reset file inputs (need to do this manually)
      document.getElementById('thumbnailFile').value = '';
      document.getElementById('fullImageFile').value = '';
      
      // Clear success message after delay
      setTimeout(() => {
        setUploadStatus(prev => ({ ...prev, success: false }));
      }, 5000);
      
    } catch (error) {
      console.error('Error uploading render:', error);
      setUploadStatus({
        uploading: false,
        success: false,
        error: "Failed to upload render. Please try again."
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this render? This action cannot be undone.')) {
      setDeleteStatus({ deleting: true, id: id, error: null });
      
      try {
        await deleteRender(id);
        
        // Remove the deleted render from the list
        setRenders(prev => prev.filter(render => render.id !== id));
        
        setDeleteStatus({ deleting: false, id: null, error: null });
        
      } catch (error) {
        console.error('Error deleting render:', error);
        setDeleteStatus({
          deleting: false,
          id: null,
          error: "Failed to delete render. Please try again."
        });
      }
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <p>Manage your Blender renders portfolio</p>
      </div>
      
      <div className="admin-content">
        <div className="upload-section">
          <h2>Upload New Render</h2>
          
          <form className="upload-form" onSubmit={handleSubmit}>
            {uploadStatus.error && (
              <div className="form-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <span>{uploadStatus.error}</span>
              </div>
            )}
            
            {uploadStatus.success && (
              <div className="form-success">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <span>Render uploaded successfully!</span>
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="title">Title <span className="required">*</span></label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description <span className="required">*</span></label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                required
              ></textarea>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="software">Software</label>
                <input
                  type="text"
                  id="software"
                  name="software"
                  value={formData.software}
                  onChange={handleInputChange}
                  placeholder="Blender"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="renderTime">Render Time</label>
                <input
                  type="text"
                  id="renderTime"
                  name="renderTime"
                  value={formData.renderTime}
                  onChange={handleInputChange}
                  placeholder="e.g. 4h 30m"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="thumbnailFile">Thumbnail Image <span className="required">*</span></label>
              <div className="file-input-wrapper">
                <input
                  type="file"
                  id="thumbnailFile"
                  name="thumbnailFile"
                  accept="image/*"
                  onChange={handleFileChange}
                  required
                />
                <div className="file-input-button">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                  <span>Choose thumbnail</span>
                </div>
                <span className="file-name">
                  {formData.thumbnailFile ? formData.thumbnailFile.name : 'No file chosen'}
                </span>
              </div>
              <p className="file-help">Recommended size: 500x500px (will be displayed in the gallery grid)</p>
            </div>
            
            <div className="form-group">
              <label htmlFor="fullImageFile">Full Image <span className="required">*</span></label>
              <div className="file-input-wrapper">
                <input
                  type="file"
                  id="fullImageFile"
                  name="fullImageFile"
                  accept="image/*"
                  onChange={handleFileChange}
                  required
                />
                <div className="file-input-button">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                  <span>Choose full image</span>
                </div>
                <span className="file-name">
                  {formData.fullImageFile ? formData.fullImageFile.name : 'No file chosen'}
                </span>
              </div>
              <p className="file-help">High resolution image for the individual render page</p>
            </div>
            
            <button 
              type="submit" 
              className="upload-button"
              disabled={uploadStatus.uploading}
            >
              {uploadStatus.uploading ? (
                <>
                  <span className="spinner-small"></span>
                  Uploading...
                </>
              ) : 'Upload Render'}
            </button>
          </form>
        </div>
        
        <div className="manage-section">
          <h2>Manage Renders</h2>
          
          {deleteStatus.error && (
            <div className="delete-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{deleteStatus.error}</span>
            </div>
          )}
          
          {loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading renders...</p>
            </div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : renders.length === 0 ? (
            <div className="no-renders">
              <p>No renders found. Upload your first render using the form above.</p>
            </div>
          ) : (
            <div className="renders-list">
              <table className="renders-table">
                <thead>
                  <tr>
                    <th>Thumbnail</th>
                    <th>Title</th>
                    <th>Date Added</th>
                    <th>Views</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {renders.map(render => (
                    <tr key={render.id}>
                      <td className="thumbnail-cell">
                        <img 
                          src={render.thumbnail} 
                          alt={render.title}
                          className="admin-thumbnail"
                        />
                      </td>
                      <td className="title-cell">{render.title}</td>
                      <td className="date-cell">
                        {new Date(render.createdAt).toLocaleDateString()}
                      </td>
                      <td className="views-cell">{render.views}</td>
                      <td className="actions-cell">
                        <a 
                          href={`/render/${render.id}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="view-button"
                          title="View render"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        </a>
                        <button 
                          className="delete-button" 
                          onClick={() => handleDelete(render.id)}
                          disabled={deleteStatus.deleting && deleteStatus.id === render.id}
                          title="Delete render"
                        >
                          {deleteStatus.deleting && deleteStatus.id === render.id ? (
                            <span className="spinner-small"></span>
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;